<template>
  <div>
    <div class="">
      <div v-if="userNotes.length">
        <div class="note-list">
          <div v-for="userNote in userNotes" :key="userNote.id" class="notes">
            <div v-if="!isEditing" class="note-wrapper">
              <div class="note">
                <!-- eslint-disable vue/no-v-html -->
                <!-- eslint-disable-next-line -->
                <p
                  v-for="noteParagraph in formatNote(userNote.note)"
                  v-html="fbLinkify(decode(noteParagraph))"
                ></p>
              </div>

              <div class="btn-cluster">
                <button
                  class="btn -main -medium -normalcase"
                  @click.prevent="openEditNoteForm(userNote)"
                >
                  Edit Note
                </button>
                <button
                  class="btn -link -medium -normalcase"
                  @click.prevent="openDeleteModal(userNote.id)"
                >
                  Remove Note
                </button>
              </div>
            </div>

            <div v-if="isEditing">
              <textarea
                :id="noteEditId(userNote.id)"
                v-model="userNote.note"
                name="note"
                class="addnoteinput"
                required
                placeholder="Type Here"
              />
              <div class="btn-cluster">
                <button class="btn -main -medium -normalcase" @click="submitEdit(userNote)">
                  Save Changes
                </button>
                <button class="btn -link -medium -normalcase" @click="cancelEdit(userNote)">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <textarea
          v-model="newNote"
          name="note"
          class="addnoteinput"
          required
          placeholder="Type Here"
          :disabled="makingApiCall"
        />
        <button
          :disabled="makingApiCall"
          class="btn -main -medium -normalcase"
          type="submit"
          @click="submitCreate"
        >
          Add Note
        </button>
      </div>
    </div>

    <modal-confirm
      v-if="isDeleteModalOpen"
      heading="Remove Note"
      subheading="Are you sure you want to remove today's note?"
      :handle-close-click="closeDeleteModal"
      :handle-confirm-click="handleDeleteNote"
    />
  </div>
</template>

<script>
// @flow

/**
 * Handles:
 * -Display of notes
 * -Creation of new note
 * -Editing of note(s)
 * -Deleting of note(s)
 *
 * Note: supports multiple notes for legacy, pre 11/5/17, version of site.
 *
 * A "Note" is both the text or body field as well as the data object with
 * id, schedule_date and note (body).
 * This is due to how the DB field was setup a long time ago.
 *
 * If there are multiple notes and you click edit, all notes will be in editing mode
 * instead of just the one you selected.  It still saves them correctly, but if an error
 * on submit not sure if it resets correctly.  Multiple notes is legacy, so probably
 * not worth the time to fix unless people complain.
 *
 * Many people add YouTube links in here.  Would be good to linkify them, although YT
 * uses so many different urls it makes it hard youtu.be youtube.com, etc
 *
 */

import modalConfirm from 'app/components/modals/modal-confirm.vue'
import { fbAPIDelete, fbAPIPost } from 'app/utils/http-api'

export default {
  name: 'Notes',
  components: { modalConfirm },
  // eslint-disable-next-line
  props: ['scheduleDate'],
  data() {
    return {
      // on load get userNotes from json in html
      userNotes: Laravel.userNotes,
      // when creating a new note the text (body) for it
      newNote: '',
      isEditing: false,
      makingApiCall: false,
      isDeleteModalOpen: false,
      // original text of note when editing to revert
      origNote: '',
      // when deleting, id of note
      deleteId: 0,
      // get FB urls.  Not 100% that this is best regex for urls, but FB urls shouldn't be too fancy
      // https://stackoverflow.com/questions/1500260/detect-urls-in-text-with-javascript
      // eslint-disable-next-line
      fbUrlRegex: /(\bhttps:\/\/www\.fitnessblender\.com\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi,
    }
  },
  methods: {
    noteEditId(id: number) {
      return 'noteEdit' + id
    },
    formatNote(note: String) {
      return note.split(/\r?\n/g)
    },
    fbLinkify(text: String) {
      return text.replace(this.fbUrlRegex, (url) => '<a href="' + url + '">' + url + '</a>')
    },
    // @see https://gomakethings.com/preventing-cross-site-scripting-attacks-when-using-innerhtml-in-vanilla-javascript/
    // Use this before adding our custom html (fb links)
    decode(html: string): string {
      this.decoder = this.decoder || document.createElement('div')
      this.decoder.textContent = html
      return this.decoder.innerHTML

      // this.decoder = this.decoder || document.createElement('div')
      // this.decoder.innerHTML = html
      // return this.decoder.textContent
    },

    submitCreate() {
      if (this.newNote === '') {
        // if note blank do nothing. Force user to add something
        // note: displaying an error msg and making border of text field red would be good
        return
      } else {
        // ? should we add a spinner to the button while submitting
        this.submitCreateToServer()
      }
    },
    submitCreateToServer() {
      this.makingApiCall = true
      fbAPIPost(
        `calendar/notes`,
        {
          note: this.newNote,
          schedule_date: this.scheduleDate,
        },
        (res) => {
          this.makingApiCall = false
          const noteId = res.body.data
          // add a new note
          this.userNotes.push({
            id: noteId,
            // eslint-disable-next-line
            schedule_date: this.scheduleDate,
            note: this.newNote,
          })
          this.newNote = ''
        },
        (err) => {
          this.makingApiCall = false
          // leave text area as it was
        },
      )
    },

    openEditNoteForm(userNote: Object) {
      this.origNote = userNote.note
      this.isEditing = true
    },
    cancelEdit(userNote: Object) {
      userNote.note = this.origNote
      this.origNote = ''
      this.isEditing = false
    },
    submitEdit(userNote: Object) {
      userNote.note = userNote.note.trim()

      if (userNote.note === '') {
        // if comment blank do nothing. Force user to add something or cancel
        // note: displaying an error msg and making border of text field red would be good
        return
      } else if (userNote.note === this.origNote) {
        // if same value, don't bother submitting to server
      } else {
        this.submitEditToServer(userNote)
        // hide editing form right away.  On error show modal and revert comment
        // note: this could potentially allow user to re-edit and save right away before
        // server handles the first one
      }
      this.isEditing = false
    },
    submitEditToServer(note: Object) {
      fbAPIPost(
        `calendar/notes/${note.id}`,
        {
          id: note.id,
          note: note.note,
        },
        (res) => {},
        (err) => {
          note.note = this.origNote
        },
      )
    },

    openDeleteModal(id: Number) {
      this.deleteId = id
      this.isDeleteModalOpen = true
    },
    closeDeleteModal() {
      this.isDeleteModalOpen = false
    },
    handleDeleteNote() {
      // modal stays open until the response from server, but won't allow multiple clicks
      fbAPIDelete(
        `calendar/notes/${this.deleteId}`,
        (res) => {
          this.userNotes = this.userNotes.filter((note) => note.id !== this.deleteId)
          this.closeDeleteModal()
        },
        (errr) => {
          this.closeDeleteModal()
        },
      )
    },
  },
}
</script>
